import * as React from "react";
import { ComboBox, Dropdown, IComboBox, IComboBoxOption, IDropdownOption, ResponsiveMode } from "@fluentui/react";
import { ChoiceProps } from "../../interfaces/props/ChoiceProps";
import { LanguageContext } from "../../../../utilities/LocalizationModule";
import { logger } from "../../../../utilities/logger";
import { SharePointMetadataBooleans } from "../../config/sharepointConstants";

const getOptions = (allowFreeForm: boolean, defaultOptions: any[], inputValue: string): IDropdownOption[] => {
  const options = [{ key: null, text: "" }, ...(defaultOptions?.map(item => ({ key: item, text: item })) || [])];
  if (allowFreeForm && inputValue) {
    if (!options.some(option => option.key && ((option.key as string).trim().toLowerCase() === inputValue.trim().toLowerCase()))) {
      options.push({ key: inputValue, text: inputValue });
    }
  }
  return options;
}

export const Choice: React.FunctionComponent<ChoiceProps> = ({
  displayName,
  isRequired,
  inputValue,
  Choices,
  updateInputValidationStatus,
  inputValidator,
  error
}: ChoiceProps) => {
  const languageStrings = React.useContext(LanguageContext);

  const [allowFreeForm] = React.useState<boolean>(inputValue?.xml?.FillInChoice === SharePointMetadataBooleans.true);
  const [options, setOptions] = React.useState<IDropdownOption[]>(getOptions(allowFreeForm, Choices, inputValue?.value ? inputValue.value as string : null));
  const [selectedKey, setSelectedKey] = React.useState<string | number | undefined>(inputValue ? inputValue?.value : undefined);

  const isFirstRun = React.useRef(true);
  React.useEffect(() => {
    try {
      if (isFirstRun.current) {
        isFirstRun.current = false;
        return;
      }
      updateInputValidationStatus(inputValidator.validate(inputValue?.value));
    } catch (e) {
      logger(e, "choice.tsx (useEffect)")
    }
  }, [inputValue?.updatedFromDataSourceToggle]);

  const onChange = React.useCallback(
    (event: React.FormEvent<HTMLDivElement>, item: IDropdownOption): void => {
      updateInputValidationStatus(inputValidator.validate(item.key));
    },
    [inputValidator]
  );

  const onComboChange = React.useCallback(
    (event: React.FormEvent<IComboBox>, option?: IComboBoxOption, index?: number, value?: string): void => {
      let key = option?.key;
      if (!option && value) {
        key = value;
        setOptions(prevOptions => [...prevOptions, { key: key!, text: value }]);
      }
      setSelectedKey(key);
      updateInputValidationStatus(inputValidator.validate(key));
    },
    [inputValidator]
  );

  return allowFreeForm
    ?
    <ComboBox
      allowFreeform
      useComboBoxAsMenuWidth
      disabled={inputValue?.xml?.ReadOnly === SharePointMetadataBooleans.true}
      selectedKey={selectedKey}
      label={displayName}
      options={options}
      onChange={onComboChange}
      required={isRequired()}
      errorMessage={error}
    />
    :
    <Dropdown
      label={displayName}
      disabled={inputValue?.xml?.ReadOnly === SharePointMetadataBooleans.true}
      selectedKey={inputValue ? inputValue?.value : undefined}
      onChange={onChange}
      placeholder={languageStrings.SelectOption}
      options={options}
      required={isRequired()}
      errorMessage={error}
      className="xb-sp-input"
      responsiveMode={ResponsiveMode.small}
    />;
};

export default Choice;
